import type { GetStaticPaths, NextPage } from 'next'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { ApiHandler } from '@hozana/api/ApiHandler'
import { removeEntity } from '@hozana/api/actions'
import { fetchApi } from '@hozana/api/fetchApi'
import { modifyQueryEntities } from '@hozana/api/functions'
import { useApiData } from '@hozana/api/hooks/useApiData'
import { createGetStaticData, createGetStaticProps } from '@hozana/api/server/static'
import { useAction } from '@hozana/hooks/useAction'
import { useDispatch, useSelector } from '@hozana/redux/hooks'
import { useRouter } from '@hozana/router'
import { getFirstQueryValue, getFullUrl, getQueryValues, translateUrl } from '@hozana/router/functions'
import { useScreen } from '@hozana/screen/useScreen'
import { IMAGE_SIZES, getImageUrl } from '@hozana/styling/image'
import { QA } from '@hozana/tests/constants'
import { EVENT_ORIGIN, TRACKING_SOURCE, UTM } from '@hozana/tracking/constants'

import { Container } from 'elements/layout/Container'
import { Div } from 'elements/layout/Div'
import { Space } from 'elements/layout/Space'
import { Loading } from 'elements/ui/Loading'

import { openPopin } from 'general/actions'
import { QUERY_ACTION } from 'general/constants'
import { POPINS } from 'general/managers/popins/constants'
import { Head } from 'general/structure/Head'
import { Page } from 'general/structure/Page'
import { TabsBar } from 'general/structure/Tabs/TabsBar'
import { useTabs } from 'general/structure/Tabs/useTabs'
import { NAMESPACE } from 'i18n/constants'
import { PAGE, newRoutes } from 'routes/constants'

import { PreviewingCommunityBanner } from 'modules/community/components/PreviewingCommunityBanner'
import { COMMUNITY_PAGE_TABS } from 'modules/community/constants'
import { communityNameTitleGetter, getUserRole, redirectToCommunitySubscription } from 'modules/community/functions'
import { useCommunitiesPropositions } from 'modules/community/hooks/useCommunitiesPropositions'
import { CommunityPageTopSection } from 'modules/community/pages/CommunityPage/CommunityPageTopSection'
import { getCommunityQuery, incrementCommunityViewsCountMutation } from 'modules/community/queries'
import { selectCommunity } from 'modules/community/selectors'
import type { TCommunity } from 'modules/community/types'
import { SHARE_OBJECTS } from 'modules/share/constants'
import { getTagsQuery } from 'modules/tag/queries'
import { selectAllTags } from 'modules/tag/selectors'
import { useUser } from 'modules/user/hooks/useUser'

import { CommunityDescriptionTab } from './CommunityDescriptionTab'
import { CommunityPageDesktopContent } from './CommunityPageDesktopContent'
import { PreloadCommunityPage } from './PreloadCommunityPage'

type TStyledDivProps = { hide?: boolean }

const StyledDiv = styled(Div)<TStyledDivProps>`
  ${(props) => props.hide && 'display: none;'};
`

const publicationMaxWidth = '712px'
const communityImageSize = IMAGE_SIZES.community.big

const getTabsBarMaxWidth = (nbTabs: number) => ({
  xs: 'initial',
  sm: nbTabs === 3 ? publicationMaxWidth : '400px',
  lg: publicationMaxWidth,
})

/** Get canonical link for the head html tag */
const getHeadLinks = (community: TCommunity) => ({
  canonical: getFullUrl(
    {
      pathname: PAGE.COMMUNITY_VIEW,
      query: {
        communityId: community.id,
        communitySlug: community.slug,
      },
    },
    community.lang,
  ),
})

export const getStaticPaths: GetStaticPaths = async () => ({ paths: [], fallback: 'blocking' })

const getCommunityPageApiQueries = (communityId: number) => ({
  community: {
    query: modifyQueryEntities(getCommunityQuery({ communityId }), {
      community: (community) =>
        community.id === communityId ? community : { ...community, description: undefined, prayerContent: undefined },
    }),
    displayErrors: true,
  },
  // Tags must be included in html: tags links are SEO links
  tags: {
    query: getTagsQuery(),
    noDataToReturn: true,
  },
})

export const getStaticProps = createGetStaticProps(
  PAGE.COMMUNITY_VIEW,
  communityNameTitleGetter,
  async (ctx) => {
    const communityId = Number(ctx.params.communityId)

    const data = await createGetStaticData(() => getCommunityPageApiQueries(communityId))(ctx)

    // Redirect to the right language if current locale is not community.lang
    if ('props' in data) {
      const community = selectCommunity(ctx.store.getState(), communityId)
      if (community && community.lang !== ctx.locale) {
        //return getRedirect(community)
      }

      // Remove useless tags
      const tags = selectAllTags(ctx.store.getState())
      tags.forEach(({ id }) => {
        if (!community.tags?.includes(id)) {
          ctx.store.dispatch(removeEntity('tag', id))
        }
      })
    }
    return data
  },
  { namespaces: [NAMESPACE.COMMUNITY, NAMESPACE.PRAY] },
)

/**
 * ### CommunityPage ###
 */
export const CommunityPage: NextPage = () => {
  const {
    i18n: { language },
    t,
  } = useTranslation()
  const screen = useScreen()
  const { pathname, query, replace } = useRouter() // #router.replace
  const dispatch = useDispatch()
  const me = useUser()
  const { communityId: stringCommunityId, communitySlug, source } = getFirstQueryValue(query)
  const communityId = parseInt(stringCommunityId, 10)

  // Increment community views count
  useEffect(() => {
    if (communityId) {
      dispatch(fetchApi(incrementCommunityViewsCountMutation({ communityId })))
    }
  }, [communityId, dispatch])

  const [, communityQuery] = useApiData(
    () => ({
      query: getCommunityQuery({ communityId }),
      disabled: !communityId,
    }),
    [communityId],
  )
  const community = useSelector((state) => selectCommunity(state, communityId))

  const [isMapDisplayed, setIsMapDisplayed] = useState(false)

  const communityImage = community && getImageUrl(community.picture, communityImageSize)
  const communityImageWidth = community && communityImageSize.split('x')[0]
  const communityImageHeight = community && communityImageSize.split('x')[1]

  const tabList = {
    [COMMUNITY_PAGE_TABS.DESCRIPTION]: 'trans:community:community.tab.description',
    [COMMUNITY_PAGE_TABS.PUBLICATIONS]: t('community:community.tab.publications', {
      nbPublications: community?.nbPublications || '0',
    }),
  }

  const { currentTab, changeTab, getTabUrl } = useTabs({
    data: tabList,
    onChange: (tabId) => {
      if (tabId === COMMUNITY_PAGE_TABS.PUBLICATIONS) {
        window.location.href = newRoutes.community(
          `${communityId}-${communitySlug}`,
          EVENT_ORIGIN.COMMUNITY_PUBLICATIONS_TAB,
        )
      }
    },
  })

  useAction(
    () =>
      dispatch(
        openPopin(POPINS.InvitePopin, {
          objectToShare: SHARE_OBJECTS.COMMUNITY,
          community,
          utmContent: UTM.CONTENT.COMMUNITY_PAGE_URL_INVITE_POPIN,
          trackingSource: TRACKING_SOURCE.EMAIL_LINK,
        }),
      ),
    [community, dispatch],
    QUERY_ACTION.INVITE,
    { disabled: !community },
  )

  useAction(
    () => {
      if (!getUserRole(community).isMember) {
        redirectToCommunitySubscription(community, me.isLogged, {
          course: 'page',
          origin: EVENT_ORIGIN.AUTOMATIC,
          source: source ?? TRACKING_SOURCE.EMAIL_LINK,
        })
      }
    },
    [community, me.isLogged, source],
    QUERY_ACTION.JOIN,
    { disabled: !community },
  )

  useAction(
    () => {
      window.location.href = translateUrl(
        {
          pathname: PAGE.COMMUNITY_VIEW,
          query: {
            communityId: community?.id,
            communitySlug: community?.slug,
            utm_source: UTM.SOURCE.SHARE,
            utm_medium: UTM.MEDIUM.MAILTO,
            utm_campaign: UTM.CAMPAIGN.COMMUNITY(community?.id, community?.slug),
            utm_content: UTM.CONTENT.PUBLICATION_MAIL_MAILTO_SHARE_BUTTON,
          },
        },
        community?.lang,
        { format: 'string' },
      )
    },
    [community?.id, community?.lang, community?.slug],
    QUERY_ACTION.SHARE_MAIL_PUBLI,
    { disabled: !community },
  )

  // Ensure correct url
  useEffect(() => {
    if (community) {
      if (communitySlug !== community.slug) {
        replace({
          pathname,
          query: {
            communityId: community.id,
            communitySlug: community.slug,
            ...getQueryValues(window.location),
          },
        })
      }
    }
  }, [community, communitySlug, pathname, replace])

  useCommunitiesPropositions({ community })

  // If no community slug is defined, wait for page reload before rendering
  if (!communitySlug) return null

  const tabContent =
    currentTab === COMMUNITY_PAGE_TABS.DESCRIPTION ? (
      <CommunityDescriptionTab community={community} />
    ) : (
      <Div data-testid={QA.COMMUNITY_PAGE.PUBLICATIONS.WRAPPER} maxW="712px" m="0 auto 90px">
        <Loading />
      </Div>
    )

  return (
    <Page>
      <ApiHandler
        queries={[communityQuery]}
        display="fullPage"
        onRetry={() => {
          communityQuery.refetch()
        }}
        noLoadingDisplay
      />
      {!community && communityQuery.loading && <PreloadCommunityPage tabList={tabList} />}
      {community && (
        <main>
          <PreviewingCommunityBanner community={community} />
          <article>
            <Head
              description={community.introduction}
              isIndexed={
                community.isSeoIndexed !== false &&
                community.lang === language &&
                currentTab === COMMUNITY_PAGE_TABS.DESCRIPTION
              }
              title={community.name}
              image={community.picture && communityImage}
              imageWidth={community.picture && communityImageWidth}
              imageHeight={community.picture && communityImageHeight}
              links={community ? getHeadLinks(community) : undefined}
              rss={{
                title: t('community:publication.publications.into.community', { community: community.name }),
                href: `${CONF_KEYS.API_URL}/${language}/rss/community/${community.id}/publications`,
              }}
            />

            {/* CONTENT */}
            <Container p={{ xs: 0, lg: '20px 10px 0' }} maxW={{ xs: 1, lg: '1010px' }}>
              <CommunityPageTopSection
                community={community}
                tabsBar={
                  <TabsBar
                    changeTab={changeTab}
                    currentTab={currentTab}
                    data={tabList}
                    getTabUrl={getTabUrl}
                    maxW={tabList && getTabsBarMaxWidth(Object.keys(tabList).length)}
                    dataQaIds={QA.COMMUNITY_PAGE.TABS(tabList)}
                    asButton
                  />
                }
              />
              {screen.xs || screen.sm || screen.md ? (
                <>
                  {currentTab === COMMUNITY_PAGE_TABS.PUBLICATIONS && (screen.sm || screen.md) && <Space />}
                  {tabContent}
                </>
              ) : (
                <CommunityPageDesktopContent community={community}>{tabContent}</CommunityPageDesktopContent>
              )}
            </Container>
          </article>
        </main>
      )}
    </Page>
  )
}
